import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { shelfLibraryService } from "@/services/shelfLibraryService";

export default function ShelfLibrary() {
  const queryClient = useQueryClient();
  const [spaceId, setSpaceId] = useState("");
  const [budget, setBudget] = useState("50");
  const [baseline, setBaseline] = useState("0");
  const [fastChars, setFastChars] = useState("120");
  const [status, setStatus] = useState("");

  const reportQuery = useQuery({
    queryKey: ["shelf-library", "report"],
    queryFn: () => shelfLibraryService.report(),
  });
  const jobsQuery = useQuery({
    queryKey: ["shelf-library", "jobs"],
    queryFn: () => shelfLibraryService.pendingJobs(),
  });

  async function run(label: string, task: () => Promise<unknown>) {
    setStatus(label);
    try {
      const result = await task();
      setStatus(typeof result === "object" && result ? JSON.stringify(result) : label);
      await queryClient.invalidateQueries({ queryKey: ["shelf-library"] });
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Failed");
    }
  }

  const report = reportQuery.data;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-6">
      <Card>
        <CardHeader>
          <CardTitle>Shelf library</CardTitle>
          <CardDescription>
            One library space is ingested and scored before ClickUp or Drive are mirrored.
            Spend after cutover is compared with the frozen weekly baseline.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>Baseline ${report?.baselineUsd.toFixed(2) ?? "0.00"}</p>
          <p>Logged spend ${report?.spentUsd.toFixed(4) ?? "0.0000"} across {report?.calls ?? 0} calls</p>
          <p>{report?.budgetAlert ? "Budget alert: logged spend is at least 10% of the monthly cap." : "Budget alert is quiet until spend reaches 10% of the monthly cap."}</p>
          <p>Synthesis: {report?.decision ?? "keep hosted librarian until this report says otherwise"}</p>
          {status ? <p className="text-muted-foreground">{status}</p> : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ingest one space</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Label htmlFor="space-id">Library space id</Label>
          <Input id="space-id" value={spaceId} onChange={(event) => setSpaceId(event.target.value)} />
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => run("Ingesting", () => shelfLibraryService.ingestLibrarySpace(spaceId.trim()))}
              disabled={!spaceId.trim()}
            >
              Ingest
            </Button>
            <Button type="button" variant="outline" onClick={() => run("Scoring", () => shelfLibraryService.runEval("library"))}>
              Run 10-question eval
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Budget, baseline, routing</CardTitle>
          <CardDescription>Max seats stay human-only. The API records model spend; this form records the cap and the console baseline.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          <Label htmlFor="budget">Monthly budget USD</Label>
          <Input id="budget" value={budget} onChange={(event) => setBudget(event.target.value)} />
          <Button type="button" onClick={() => run("Budget saved", () => shelfLibraryService.saveBudget(Number(budget) || 0, true))}>
            Save budget
          </Button>
          <Label htmlFor="baseline">This week baseline USD</Label>
          <Input id="baseline" value={baseline} onChange={(event) => setBaseline(event.target.value)} />
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              run("Baseline saved", () =>
                shelfLibraryService.saveBaseline({
                  provider: "claude",
                  spendUsd: Number(baseline) || 0,
                  capHits: 0,
                  note: "Console cap hits the API cannot see. Max seats are humans only.",
                }),
              )
            }
          >
            Save baseline
          </Button>
          <Label htmlFor="fast-chars">Fast-tier max characters</Label>
          <Input id="fast-chars" value={fastChars} onChange={(event) => setFastChars(event.target.value)} />
          <Button type="button" variant="outline" onClick={() => run("Routing saved", () => shelfLibraryService.saveRouting(Number(fastChars) || 120))}>
            Save routing
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              run("Decision saved", () =>
                shelfLibraryService.recordDecision(
                  "keep_hosted",
                  report && report.spentUsd > report.baselineUsd && report.baselineUsd > 0
                    ? "Logged librarian spend is above the baseline, so synthesis stays hosted until a later report."
                    : "Hosted librarian stays the default. Self-host synthesis only if a later report shows it dominates spend.",
                ),
              )
            }
          >
            Record keep-hosted decision
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Waiting on Slack</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(jobsQuery.data ?? []).length === 0 ? <p className="text-sm text-muted-foreground">None</p> : null}
          {(jobsQuery.data ?? []).map((job) => (
            <div key={job.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="truncate">{job.request || job.id}</span>
              <Button type="button" size="sm" onClick={() => run("Approved", () => shelfLibraryService.approveJob(job.id))}>
                Approve
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
